import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { buildReport, DOMAIN_LABELS, RECOMMENDATION_LABELS, STATUS_LABELS } from './report';
import {
  displayStatus,
  topics,
  type DomainKey,
  type InterviewEngine,
  type InterviewState,
  type Recommendation,
  type SourceKind,
  type UnderstandingItem,
} from './model';

/**
 * Écran d'entretien d'implémentation (POC §10.1 à §10.9) : la conversation à gauche, ce
 * qu'AYUMIA a compris au centre, la readiness à droite ; onglets sur écran étroit. L'écran affiche
 * l'état que rend le moteur et n'en déduit rien : les statuts sont une projection des faits, la
 * readiness vient d'un calcul déclaré, chaque recommandation cite ses faits. Le client garde la
 * main : il confirme, corrige, écarte, et décide de chaque recommandation. Un consultant humain
 * peut rejoindre l'entretien mené par l'IA.
 */

const SOURCE_LABELS: Readonly<Record<SourceKind, string>> = {
  official_registry: 'Registre officiel',
  company_website: 'Site web de l’entreprise',
  booking_form: 'Formulaire de rendez-vous',
  interview: 'Vous, pendant l’entretien',
  customer_correction: 'Votre correction',
  ai_inference: 'Déduction d’AYUMIA',
};

const DOMAIN_ORDER: readonly DomainKey[] = ['company', 'organization', 'products', 'customers', 'sales', 'purchasing', 'inventory', 'production', 'quality', 'traceability', 'accounting', 'systems'];

type Tab = 'conversation' | 'understanding' | 'readiness';

function percent(value: number): string {
  return `${Math.round(value * 100)} %`;
}

export function InterviewPage({ engine }: { engine: InterviewEngine }) {
  const [state, setState] = useState<InterviewState | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<Tab>('conversation');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    engine.start().then(
      (initial) => active && setState(initial),
      () => active && setError('L’entretien n’a pas pu démarrer.'),
    );
    return () => {
      active = false;
    };
  }, [engine]);

  const run = async (action: () => Promise<InterviewState>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setState(await action());
    } catch {
      setError('AYUMIA n’a pas pu traiter cette action. Votre réponse est conservée ; réessayez.');
    } finally {
      setBusy(false);
    }
  };

  if (error !== null && state === null)
    return (
      <main className="ay-interview">
        <div className="ay-banner ay-banner--danger" role="alert">
          {error}
        </div>
      </main>
    );
  if (state === null)
    return (
      <main className="ay-interview" aria-busy="true">
        <p className="ay-text--muted">Préparation de l’entretien…</p>
      </main>
    );

  if (state.status === 'review') return <ReviewPage state={state} />;

  return (
    <main className="ay-interview" aria-label="Entretien AYUMIA">
      <header className="ay-interview__header">
        <div>
          <p className="ay-booking__brand">AYUMIA</p>
          <h1 className="ay-interview__title">Entretien de préparation · {state.company}</h1>
        </div>
        <div className="ay-interview__participants" aria-label="Participants">
          <span className="ay-interview__participant ay-interview__participant--ai">AYUMIA (IA)</span>
          <span className="ay-interview__participant">Vous</span>
          {state.consultant_present ? (
            <span className="ay-interview__participant ay-interview__participant--human">Consultant AYUMIA</span>
          ) : (
            <button type="button" className="ay-button ay-button--secondary" onClick={() => run(() => engine.inviteConsultant())} disabled={busy}>
              + Inviter un consultant
            </button>
          )}
        </div>
        <div className="ay-interview__header-actions">
          <span className={`ay-badge${state.status === 'ready_for_review' ? ' ay-badge--success' : ''}`}>{state.status === 'ready_for_review' ? 'Prêt pour la synthèse' : 'En cours'}</span>
        </div>
      </header>
      {state.simulated && <p className="ay-interview__demo">Démonstration : les réponses d’AYUMIA suivent un scénario écrit à l’avance, sur une entreprise fictive.</p>}
      {error !== null && (
        <div className="ay-banner ay-banner--danger" role="alert">
          {error}
        </div>
      )}

      <div className="ay-interview__tabs" role="tablist" aria-label="Zones de l’entretien">
        {(
          [
            ['conversation', 'Conversation'],
            ['understanding', 'Compréhension'],
            ['readiness', `Préparation ${state.readiness.overall} %`],
          ] as const
        ).map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className="ay-interview__tab" onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      <div className="ay-interview__grid">
        <section className="ay-interview__panel" data-active={tab === 'conversation'} aria-labelledby="ay-panel-conversation">
          <h2 id="ay-panel-conversation" className="ay-interview__panel-title">
            Conversation
          </h2>
          <Conversation state={state} busy={busy} engine={engine} run={run} />
        </section>
        <section className="ay-interview__panel" data-active={tab === 'understanding'} aria-labelledby="ay-panel-understanding">
          <h2 id="ay-panel-understanding" className="ay-interview__panel-title">
            Ce qu’AYUMIA a compris
          </h2>
          <Understanding state={state} busy={busy} engine={engine} run={run} />
        </section>
        <section className="ay-interview__panel" data-active={tab === 'readiness'} aria-labelledby="ay-panel-readiness">
          <h2 id="ay-panel-readiness" className="ay-interview__panel-title">
            Préparation
          </h2>
          <ReadinessPanel state={state} onReview={() => run(() => engine.review())} busy={busy} />
        </section>
      </div>
    </main>
  );
}

type Run = (action: () => Promise<InterviewState>) => Promise<void>;

function Conversation({ state, busy, engine, run }: { state: InterviewState; busy: boolean; engine: InterviewEngine; run: Run }) {
  const [draft, setDraft] = useState('');
  const [why, setWhy] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);
  const question = state.current_question;

  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [state.messages.length, busy]);

  const send = (event?: FormEvent): void => {
    event?.preventDefault();
    const text = draft.trim();
    if (text === '' || busy) return;
    setDraft('');
    setWhy(null);
    void run(() => engine.answer(text));
  };

  const onKey = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const itemLabel = (id: string): string => state.items.find((item) => item.id === id)?.label ?? id;

  return (
    <div className="ay-chat">
      <div className="ay-chat__log" role="log" aria-live="polite" aria-label="Messages">
        {state.messages.map((message) => (
          <div key={message.id} className={`ay-chat__message ay-chat__message--${message.role}`}>
            {message.role !== 'system' && <span className="ay-chat__author">{message.role === 'ai' ? 'AYUMIA' : message.role === 'consultant' ? 'Consultant' : 'Vous'}</span>}
            <p>{message.text}</p>
            {message.question_id !== undefined && message.question_id === question?.candidate.id && why === message.question_id && (
              <div className="ay-chat__why" role="note">
                <strong>Pourquoi cette question ?</strong>
                <p>{question.candidate.objective}</p>
                {question.candidate.targeted.length > 0 && <p className="ay-text--muted">Elle complète : {question.candidate.targeted.map(itemLabel).join(', ')}.</p>}
              </div>
            )}
          </div>
        ))}
        {busy && (
          <div className="ay-chat__message ay-chat__message--ai ay-chat__thinking" role="status">
            <span className="ay-chat__author">AYUMIA</span>
            <p>AYUMIA analyse votre réponse…</p>
          </div>
        )}
        <div ref={end} />
      </div>
      {state.status === 'in_progress' && (
        <div className="ay-chat__actions" aria-label="Actions rapides">
          <button type="button" className="ay-booking__choice" disabled={busy} onClick={() => run(() => engine.defer('dont_know'))}>
            Je ne sais pas
          </button>
          <button type="button" className="ay-booking__choice" disabled={busy} onClick={() => run(() => engine.defer('later'))}>
            À confirmer plus tard
          </button>
          <button type="button" className="ay-booking__choice" disabled={question === undefined} aria-pressed={why !== null} onClick={() => setWhy((current) => (current === null ? (question?.candidate.id ?? null) : null))}>
            Pourquoi cette question ?
          </button>
        </div>
      )}
      {state.status === 'in_progress' && (
        <form className="ay-chat__composer" onSubmit={send}>
          <label className="ay-visually-hidden" htmlFor="ay-chat-input">
            Votre réponse
          </label>
          <textarea id="ay-chat-input" rows={3} value={draft} placeholder="Votre réponse… (Entrée pour envoyer, Maj+Entrée pour aller à la ligne)" onChange={(event) => setDraft(event.target.value)} onKeyDown={onKey} />
          <div className="ay-chat__composer-row">
            {state.simulated && state.suggested_reply !== undefined && (
              <button type="button" className="ay-button ay-button--secondary" onClick={() => setDraft(state.suggested_reply ?? '')} disabled={busy}>
                Réponse type (démo)
              </button>
            )}
            <button type="submit" className="ay-button" disabled={busy || draft.trim() === ''}>
              Envoyer
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Understanding({ state, busy, engine, run }: { state: InterviewState; busy: boolean; engine: InterviewEngine; run: Run }) {
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const coverage = topics(state);
  const visible = state.items.filter((item) => item.validation_status !== 'superseded');
  const grouped = DOMAIN_ORDER.map((domain) => ({ domain, items: visible.filter((item) => item.domain === domain) })).filter((group) => group.items.length > 0);
  const openRecommendations = state.recommendations.filter((rec) => rec.decision === undefined);
  const labelOf = (id: string): string => state.items.find((item) => item.id === id)?.label ?? id;

  return (
    <div className="ay-understanding">
      <div className="ay-coverage" aria-label="Avancement par sujet">
        <CoverageGroup title="Compris" marker="✓" domains={coverage.done} />
        <CoverageGroup title="En cours" marker="●" domains={coverage.current} />
        <CoverageGroup title="À explorer" marker="○" domains={coverage.todo} />
      </div>

      {openRecommendations.map((rec) => (
        <RecommendationCard key={rec.id} rec={rec} labelOf={labelOf} busy={busy} onDecide={(decision) => run(() => engine.decide(rec.id, decision))} />
      ))}

      {grouped.map((group) => (
        <section key={group.domain} className="ay-understanding__domain" aria-labelledby={`ay-domain-${group.domain}`}>
          <h3 id={`ay-domain-${group.domain}`}>{DOMAIN_LABELS[group.domain]}</h3>
          <ul>
            {group.items.map((item) => {
              const status = displayStatus(item);
              const expanded = open === item.id;
              return (
                <li key={item.id} className={`ay-item ay-item--${status}`}>
                  <button type="button" className="ay-item__row" aria-expanded={expanded} onClick={() => setOpen(expanded ? null : item.id)}>
                    <span className="ay-item__label">{item.label}</span>
                    <span className="ay-item__value">{item.value ?? (item.deferred === true ? 'Reporté' : '—')}</span>
                    <span className={`ay-status ay-status--${status}`}>{STATUS_LABELS[status]}</span>
                  </button>
                  {expanded && (
                    <div className="ay-item__detail">
                      <ItemDetail item={item} labelOf={labelOf} />
                      {editing?.id === item.id ? (
                        <form
                          className="ay-item__edit"
                          onSubmit={(event) => {
                            event.preventDefault();
                            if (editing.value.trim() === '') return;
                            const value = editing.value.trim();
                            setEditing(null);
                            void run(() => engine.correct(item.id, value));
                          }}
                        >
                          <label className="ay-field">
                            <span className="ay-field__label">Valeur correcte</span>
                            <input autoFocus value={editing.value} onChange={(event) => setEditing({ id: item.id, value: event.target.value })} />
                          </label>
                          <div className="ay-stack">
                            <button type="submit" className="ay-button" disabled={busy || editing.value.trim() === ''}>
                              Enregistrer la correction
                            </button>
                            <button type="button" className="ay-button ay-button--secondary" onClick={() => setEditing(null)}>
                              Annuler
                            </button>
                          </div>
                        </form>
                      ) : (
                        <div className="ay-stack">
                          {(status === 'to_confirm' || status === 'inferred' || status === 'conflicting') && (
                            <button type="button" className="ay-button" disabled={busy} onClick={() => run(() => engine.confirm(item.id))}>
                              C’est exact
                            </button>
                          )}
                          <button type="button" className="ay-button ay-button--secondary" disabled={busy} onClick={() => setEditing({ id: item.id, value: item.value ?? '' })}>
                            {item.value === undefined ? 'Renseigner' : 'Corriger'}
                          </button>
                          {item.value !== undefined && status !== 'confirmed' && (
                            <button type="button" className="ay-button ay-button--secondary" disabled={busy} onClick={() => run(() => engine.reject(item.id))}>
                              Écarter
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function CoverageGroup({ title, marker, domains }: { title: string; marker: string; domains: DomainKey[] }) {
  return (
    <div className="ay-coverage__group">
      <h3>{title}</h3>
      {domains.length === 0 ? (
        <p className="ay-text--muted">—</p>
      ) : (
        <ul>
          {domains.map((domain) => (
            <li key={domain}>
              <span aria-hidden="true">{marker}</span> {DOMAIN_LABELS[domain]}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ItemDetail({ item, labelOf }: { item: UnderstandingItem; labelOf: (id: string) => string }) {
  return (
    <dl className="ay-item__facts">
      {item.source !== undefined && (
        <div>
          <dt>Source</dt>
          <dd>{SOURCE_LABELS[item.source]}</dd>
        </div>
      )}
      {item.statement !== undefined && item.source !== 'customer_correction' && (
        <div>
          <dt>{item.source === 'interview' ? 'Ce que vous avez dit' : 'Origine'}</dt>
          <dd>{item.source === 'interview' ? <q>{item.statement}</q> : item.statement}</dd>
        </div>
      )}
      {item.reason !== undefined && (
        <div>
          <dt>Raisonnement</dt>
          <dd>{item.reason}</dd>
        </div>
      )}
      {item.confidence !== undefined && (
        <div>
          <dt>Confiance indiquée</dt>
          <dd>
            {percent(item.confidence)} <span className="ay-text--muted">(indicative, pas une probabilité)</span>
          </dd>
        </div>
      )}
      {(item.conflicts_with?.length ?? 0) > 0 && (
        <div>
          <dt>Contredit</dt>
          <dd>{item.conflicts_with!.map(labelOf).join(', ')}</dd>
        </div>
      )}
      {item.value === undefined && (
        <div>
          <dt>État</dt>
          <dd>{item.deferred === true ? 'Reporté à plus tard.' : 'Pas encore abordé.'}</dd>
        </div>
      )}
    </dl>
  );
}

function RecommendationCard({ rec, labelOf, busy, onDecide }: { rec: Recommendation; labelOf: (id: string) => string; busy: boolean; onDecide: (decision: NonNullable<Recommendation['decision']>) => void }) {
  const [explain, setExplain] = useState(false);
  const label = rec.recommendation.map((kind) => RECOMMENDATION_LABELS[kind]).join(' + ');
  return (
    <article className="ay-reco" aria-labelledby={`ay-reco-${rec.id}`}>
      <p className="ay-reco__kicker">Recommandation · {DOMAIN_LABELS[rec.domain]}</p>
      <h3 id={`ay-reco-${rec.id}`}>{rec.title}</h3>
      <p className="ay-reco__verdict">
        <span className="ay-badge">{label}</span>
        {rec.likely !== undefined && <span className="ay-text--muted"> → probablement {RECOMMENDATION_LABELS[rec.likely].toLowerCase()}</span>}
        <span className="ay-text--muted"> · aujourd’hui : {rec.current}</span>
      </p>
      <p>{rec.rationale}</p>
      {explain && (
        <div className="ay-reco__evidence">
          <p>
            <strong>Fondée sur :</strong> {rec.evidence_ids.map(labelOf).join(', ')}.
          </p>
          <p className="ay-text--muted">Confiance indiquée : {percent(rec.confidence)} (indicative). C’est vous qui décidez.</p>
        </div>
      )}
      <div className="ay-stack">
        <button type="button" className="ay-button ay-button--secondary" aria-expanded={explain} onClick={() => setExplain((value) => !value)}>
          Comprendre
        </button>
        <button type="button" className="ay-button ay-button--secondary" disabled={busy} onClick={() => onDecide('keep_current')}>
          Garder l’existant
        </button>
        <button type="button" className="ay-button" disabled={busy} onClick={() => onDecide(rec.recommendation.includes('keep') ? 'accepted' : 'study_ayumia')}>
          {rec.recommendation.includes('keep') ? 'D’accord' : 'Étudier avec AYUMIA'}
        </button>
      </div>
    </article>
  );
}

function ReadinessPanel({ state, onReview, busy }: { state: InterviewState; onReview: () => void; busy: boolean }) {
  const [open, setOpen] = useState<DomainKey | null>(null);
  const { readiness } = state;
  const gained = readiness.overall - state.initial_overall;
  const decisions = state.recommendations.filter((rec) => rec.decision === undefined).length + state.items.filter((item) => item.deferred === true && item.value === undefined).length;
  return (
    <div className="ay-readiness">
      <p className="ay-readiness__question">AYUMIA dispose-t-il de suffisamment d’informations pour préparer votre environnement ?</p>
      <div className="ay-readiness__overall">
        <span className="ay-readiness__value">{readiness.overall} %</span>
        <span className="ay-text--muted">Préparation globale{gained > 0 ? ` · +${gained} points depuis le début` : ''}</span>
      </div>
      <ul className="ay-readiness__domains">
        {readiness.domains.map((domain) => {
          const expanded = open === domain.domain;
          return (
            <li key={domain.domain}>
              <button type="button" className="ay-readiness__row" aria-expanded={expanded} onClick={() => setOpen(expanded ? null : domain.domain)}>
                <span>{DOMAIN_LABELS[domain.domain]}</span>
                <span className="ay-readiness__bar" aria-hidden="true">
                  <span style={{ inlineSize: `${domain.score}%` }} data-blocked={domain.blocking.length > 0} />
                </span>
                <span className="ay-readiness__score">{domain.score} %</span>
              </button>
              {expanded && (
                <div className="ay-readiness__explain">
                  {domain.covered.length > 0 && <p>Couvert : {domain.covered.join(', ')}.</p>}
                  {domain.partial.length > 0 && <p>À confirmer : {domain.partial.join(', ')}.</p>}
                  {domain.missing.length > 0 && <p>Manquant : {domain.missing.join(', ')}.</p>}
                  {domain.blocking.length > 0 && <p className="ay-field__error">Bloquant : {domain.blocking.join(', ')}. Le domaine reste plafonné à 50 % tant que ce point n’est pas levé.</p>}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <div className="ay-readiness__summary">
        <p>
          <strong>{readiness.blockers.length}</strong> point{readiness.blockers.length > 1 ? 's' : ''} bloquant{readiness.blockers.length > 1 ? 's' : ''}
          {readiness.blockers.length > 0 && <span className="ay-text--muted"> : {readiness.blockers.join(', ')}</span>}
        </p>
        <p>
          <strong>{decisions}</strong> décision{decisions > 1 ? 's' : ''} à confirmer
        </p>
      </div>
      <p className="ay-field__help">Chaque pourcentage vient de règles de couverture déclarées : cliquez sur un domaine pour voir ce qui est couvert ou manquant.</p>
      {state.status === 'ready_for_review' && (
        <button type="button" className="ay-button ay-booking__cta" onClick={onReview} disabled={busy}>
          Voir la synthèse
        </button>
      )}
    </div>
  );
}

function ReviewPage({ state }: { state: InterviewState }) {
  const [proposal, setProposal] = useState(false);
  const report = buildReport(state);
  return (
    <main className="ay-interview ay-review" aria-labelledby="ay-review-title">
      <p className="ay-booking__brand">AYUMIA</p>
      <h1 id="ay-review-title">Synthèse de l’entretien · {report.company}</h1>
      {state.simulated && <p className="ay-interview__demo">Démonstration : synthèse d’un scénario fictif.</p>}

      <section className="ay-card ay-review__section" aria-labelledby="ay-review-understood">
        <h2 id="ay-review-understood">Ce qu’AYUMIA a compris</h2>
        <ReportSections report={report} />
      </section>

      <section className="ay-card ay-review__section" aria-labelledby="ay-review-scope">
        <h2 id="ay-review-scope">Périmètre AYUMIA proposé</h2>
        <ul className="ay-review__chips">
          {report.proposed_scope.map((label) => (
            <li key={label} className="ay-badge">
              AYUMIA {label}
            </li>
          ))}
        </ul>
      </section>

      <section className="ay-card ay-review__section" aria-labelledby="ay-review-systems">
        <h2 id="ay-review-systems">Stratégie pour les systèmes existants</h2>
        <SystemsTable report={report} />
      </section>

      <section className="ay-card ay-review__section" aria-labelledby="ay-review-open">
        <h2 id="ay-review-open">Décisions restantes</h2>
        <OpenPoints report={report} />
      </section>

      <section className="ay-card ay-review__section" aria-labelledby="ay-review-readiness">
        <h2 id="ay-review-readiness">Préparation : {report.readiness.overall} %</h2>
        <ul className="ay-review__readiness">
          {report.readiness.domains.map((domain) => (
            <li key={domain.label}>
              {domain.label} : {domain.score} %
            </li>
          ))}
        </ul>
      </section>

      <div className="ay-booking__actions">
        <button type="button" className="ay-button ay-booking__cta" onClick={() => setProposal(true)} aria-expanded={proposal}>
          Préparer la proposition de configuration AYUMIA
        </button>
        {proposal && (
          <section className="ay-card ay-review__section" aria-labelledby="ay-review-proposal">
            <h2 id="ay-review-proposal">Proposition de configuration (aperçu)</h2>
            <p className="ay-banner ay-banner--warning">Cette proposition n’est pas installée. Elle sera relue et validée avant toute configuration.</p>
            <pre className="ay-debug__json">{JSON.stringify(report, null, 2)}</pre>
          </section>
        )}
      </div>
    </main>
  );
}

/** Sections du compte rendu : partagées avec le back-office. */
export function ReportSections({ report, exclude = [] }: { report: ReturnType<typeof buildReport>; exclude?: readonly DomainKey[] }) {
  return (
    <div className="ay-report">
      {report.sections.filter((section) => !exclude.includes(section.domain)).map((section) => (
        <section key={section.domain} aria-labelledby={`ay-report-${section.domain}`}>
          <h3 id={`ay-report-${section.domain}`}>{section.label}</h3>
          <dl className="ay-report__lines">
            {section.lines.map((entry) => (
              <div key={entry.id}>
                <dt>{entry.label}</dt>
                <dd>
                  {entry.value} {entry.status !== 'confirmed' && <span className={`ay-status ay-status--${entry.status}`}>{STATUS_LABELS[entry.status]}</span>}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}

export function SystemsTable({ report }: { report: ReturnType<typeof buildReport> }) {
  if (report.systems.length === 0) return <p className="ay-text--muted">Aucune recommandation pour l’instant.</p>;
  return (
    <table className="ay-table">
      <thead>
        <tr>
          <th scope="col">Aujourd’hui</th>
          <th scope="col">Sujet</th>
          <th scope="col">Stratégie</th>
        </tr>
      </thead>
      <tbody>
        {report.systems.map((system) => (
          <tr key={system.id}>
            <td>{system.current}</td>
            <td>{system.domain}</td>
            <td>{system.strategy}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function OpenPoints({ report }: { report: ReturnType<typeof buildReport> }) {
  if (report.open_items.length === 0 && report.open_decisions.length === 0) return <p>Aucune.</p>;
  return (
    <ul>
      {report.open_items.map((item) => (
        <li key={item.id}>
          {item.domain} : {item.label}
          {item.deferred ? ' (reporté)' : ''}
        </li>
      ))}
      {report.open_decisions.map((title) => (
        <li key={title}>{title}</li>
      ))}
    </ul>
  );
}
